/**
 * PropertyDetailPage — route `/properties/:id` (PMS portal).
 *
 * SCRUM-170 (P1.2) — Detail page chrome + Overview tab + Status modal.
 * Replaces the SCRUM-168 P0 NotConnectedYet placeholder.
 *
 * Wired surfaces:
 *   - Hero card with type tile, alias, status badge, name, address,
 *     action row (Preview / AI assist / Edit status / New unit),
 *     4-stat strip (Units / Spaces / Photos / Bookable)
 *   - SectionPill sticky tablist with 9 tabs (Overview wired this
 *     ticket; the other 8 render `<NotConnectedYet>` placeholders
 *     citing their follow-up tickets)
 *   - Overview tab: Identity card (DetailRow entries) + Visibility
 *     card (Badge + OTAChip row — OTAChips static "off" since
 *     channels aren't wired this ticket) + Description card (read-
 *     only — Edit/AI deferred to SCRUM-176 P2.3)
 *   - UnitStatusModal with the BE-aligned hard guard
 *     (Inactive + Bookable forbidden per STATUS_MATRIX.md §3)
 *
 * The `:id` param is captured via useParams; the page renders a
 * 404 fallback when the BE returns not-found.
 *
 * Per-tab follow-ups (replace each NotConnectedYet body):
 *   SCRUM-171 P1.3 Pricing
 *   SCRUM-172 P1.4 Owner
 *   SCRUM-173 P1.5 Lifecycle / Archive
 *   SCRUM-174 P2.1 Spaces + Units + SIM
 *   SCRUM-175 P2.2 Channels
 *   SCRUM-176 P2.3 Content + AI
 *   SCRUM-177 P2.4 Amenities + Compliance + History
 */

import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Icon } from '@sparta/ui-kit';
import { useSrAnnouncement } from '../../../shared/hooks/useSrAnnouncement.js';
import { useProperty } from '../api/use-property-detail.js';
import type { PropertyView } from '../api/types.js';
import { AmenitiesPanel } from '../features/amenities/AmenitiesPanel.js';
import { PropertyHero } from '../_components/PropertyHero.js';
import { SectionPill } from '../_components/SectionPill.js';
import { DetailRow } from '../_components/DetailRow.js';
import { UnitStatusModal } from '../_components/UnitStatusModal.js';
import { NotConnectedYet } from '../../../entities/not-connected-yet/index.js';
import { SimDiagram, MOCK_SIM_DEMO } from '../_components/SimDiagram.js';
import { OwnershipCard } from '../_components/OwnershipCard.js';
import { OwnershipHistory } from '../_components/OwnershipHistory.js';
import { OpenOwnershipModal } from '../_components/OpenOwnershipModal.js';
import { EndOwnershipModal } from '../_components/EndOwnershipModal.js';
import { TransferOwnershipModal } from '../_components/TransferOwnershipModal.js';
import { AmendShareModal } from '../_components/AmendShareModal.js';
import { OWNERSHIP_LITE_MODE } from '../_components/ownershipMode.js';
import { useOrgUsers } from '../api/use-org-users.js';
import { useOwnershipCurrent, useOwnershipHistory } from '../api/use-ownership.js';
import { RequirePermission } from '../../../shared/auth/RequirePermission.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { env } from '../../../shared/env.js';

type DetailSection =
  | 'overview'
  | 'spaces'
  | 'units'
  | 'content'
  | 'pricing'
  | 'amenities'
  | 'channels'
  | 'compliance'
  | 'history'
  | 'owner'
  | 'lifecycle';

const SECTIONS: readonly { id: DetailSection; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'spaces', label: 'Spaces' },
  { id: 'units', label: 'Units' },
  { id: 'content', label: 'Content' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'amenities', label: 'Amenities' },
  { id: 'channels', label: 'Channels' },
  { id: 'compliance', label: 'Compliance' },
  { id: 'history', label: 'History' },
  // SCRUM-172 P1.4 + SCRUM-173 P1.5 — Two FE-added tabs not in the
  // bundle's PROPERTY_DETAIL_SPEC.md but called out in the SCRUM-167
  // epic description ("new tab; fills the bundle gap" / "new tab per
  // FE call"). Owner consumes the existing ownership endpoints (7
  // operations); Lifecycle consumes `DELETE /properties/:id`.
  { id: 'owner', label: 'Owner' },
  { id: 'lifecycle', label: 'Lifecycle' },
];

/**
 * Per-section follow-up ticket map for the NotConnectedYet placeholders.
 *
 * Cites the ticket that owns the **remaining wire-up dependency** for
 * each surface. For most sections the BE entity is missing today so
 * we cite the BE handoff; for Owner + Lifecycle the BE exists and the
 * remaining work is the FE wiring under the original P1 ticket.
 * Mapping:
 *   - Spaces/Units composition → SCRUM-188 (Spaces+Units BE entity)
 *   - Content / AI overrides → SCRUM-186 (AI endpoint + override DTO)
 *   - Pricing → SCRUM-185 (contracts regen for baseline DTO)
 *   - Channels → SCRUM-187 (channel DTO + sync endpoints)
 *   - Compliance → SCRUM-189 (compliance DTO fields)
 *   - History → SCRUM-190 (audit feed endpoint)
 *   - Owner → **SCRUM-172** (P1.4). The 7 property-ownership
 *     endpoints already exist on
 *     `apps/api/src/modules/property/interface/property-ownership.controller.ts`
 *     and the SCRUM-167 plan §SCRUM-172 explicitly scopes
 *     "wire ownership endpoints" — so the wire-up dependency is
 *     property-bounded FE work under 172, not Companies BE
 *     (SCRUM-192). Codex round-3 caught the earlier misroute.
 *   - Lifecycle (archive) → SCRUM-173 (FE follow-up owns the
 *     DELETE wire-up once @artemis/contracts regens)
 */
const SECTION_FOLLOWUP: Record<DetailSection, string | undefined> = {
  overview: undefined, // wired
  spaces: 'SCRUM-188',
  units: 'SCRUM-188',
  content: 'SCRUM-186',
  pricing: 'SCRUM-185',
  amenities: undefined, // wired by SCRUM-141
  channels: 'SCRUM-187',
  compliance: 'SCRUM-189',
  history: 'SCRUM-190',
  owner: 'SCRUM-172',
  lifecycle: 'SCRUM-173',
};

export function PropertyDetailPage(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const result = useProperty(id);
  const [active, setActive] = React.useState<DetailSection>('overview');
  const [statusModalOpen, setStatusModalOpen] = React.useState(false);

  // Per ACCESSIBILITY.md §6: "Section pill changed → '{section} section'".
  // Skip the initial mount announcement (the user just navigated to
  // the page; SR is already hearing the H1).
  const sr = useSrAnnouncement();
  const previousActive = React.useRef<DetailSection | null>(null);
  React.useEffect(() => {
    if (previousActive.current !== null && previousActive.current !== active) {
      const label = SECTIONS.find((s) => s.id === active)?.label ?? '';
      sr.polite(`${label} section`);
    }
    previousActive.current = active;
  }, [active, sr]);

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-12 sm:py-8">
      {sr.regions}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/properties')}
        className="mb-3"
      >
        <Icon name="chevLeft" size={14} aria-hidden />
        <span className="ml-2">Properties</span>
      </Button>

      {result.status === 'loading' ? (
        <DetailLoadingSkeleton />
      ) : result.status === 'not-found' ? (
        <DetailNotFound onBack={() => navigate('/properties')} />
      ) : result.status === 'forbidden' ? (
        <DetailForbidden />
      ) : result.status === 'error' ? (
        <DetailError onRetry={result.refetch} />
      ) : (
        <>
          <PropertyHero
            property={result.data}
            onEditStatus={() => setStatusModalOpen(true)}
          />

          <div className="mb-4">
            <SectionPill<DetailSection>
              label="Property sections"
              segments={SECTIONS}
              active={active}
              onSelect={setActive}
            />
          </div>

          <section
            // The active section's heading is provided by each
            // sub-section component; the wrapper is just a layout
            // container, so no aria-labelledby here.
            aria-live="polite"
            aria-atomic="false"
          >
            {active === 'overview' ? (
              <OverviewTab property={result.data} />
            ) : active === 'pricing' ? (
              <PricingTab property={result.data} />
            ) : active === 'spaces' ? (
              <SpacesTab />
            ) : active === 'units' ? (
              <UnitsTab />
            ) : active === 'channels' ? (
              <ChannelsTab />
            ) : active === 'content' ? (
              <ContentTab property={result.data} />
            ) : active === 'amenities' ? (
              <AmenitiesTab propertyId={result.data.id} />
            ) : active === 'compliance' ? (
              <ComplianceTab />
            ) : active === 'history' ? (
              <HistoryTab />
            ) : active === 'owner' ? (
              <OwnerTab propertyId={result.data.id} />
            ) : active === 'lifecycle' ? (
              <LifecycleTab property={result.data} />
            ) : (
              <NotConnectedYet
                surface={`${SECTIONS.find((s) => s.id === active)?.label ?? ''} tab`}
                ticket={SECTION_FOLLOWUP[active]}
              />
            )}
          </section>

          {statusModalOpen ? (
            <UnitStatusModal
              property={result.data}
              onClose={() => setStatusModalOpen(false)}
            />
          ) : null}
        </>
      )}
    </div>
  );
}

interface OverviewTabProps {
  readonly property: PropertyView;
}

function OverviewTab({ property }: OverviewTabProps): React.ReactElement {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card padding="md">
        <h2
          className="m-0 mb-2 text-base font-semibold"
          style={{ color: 'var(--fg-primary)' }}
        >
          Identity
        </h2>
        <DetailRow label="Internal name" value={property.name} />
        {property.nickname !== undefined ? (
          <DetailRow label="Nickname" value={property.nickname} />
        ) : null}
        <DetailRow label="Type" value={property.type} />
        {property.alias !== undefined ? (
          <DetailRow label="Alias" value={property.alias} mono />
        ) : null}
        <DetailRow
          label="Address"
          value={
            <>
              {property.address.line1}
              <br />
              {property.address.city}, {property.address.stateOrProvince}{' '}
              {property.address.postalCode}
            </>
          }
        />
      </Card>

      <Card padding="md">
        <h2
          className="m-0 mb-2 text-base font-semibold"
          style={{ color: 'var(--fg-primary)' }}
        >
          Visibility
        </h2>
        {/* SCRUM-167 doctrine "never with fake data": the BE DTO
            doesn't carry `websiteVisible` / `instantBook` / channel
            sync state today. Earlier passes shipped static
            "Visible"/"Off" badges that read as real data — replaced
            with the slice's NotConnectedYet primitive. When the DTO
            grows these fields the body swaps to wired DetailRows.
            Tracked under SCRUM-191 (Visibility / OTA-state DTO). */}
        <NotConnectedYet
          surface="Visibility (website / instant book / OTA coverage)"
          ticket="SCRUM-191"
        />
      </Card>

      <Card padding="md" className="md:col-span-2">
        <h2
          className="m-0 mb-2 text-base font-semibold"
          style={{ color: 'var(--fg-primary)' }}
        >
          Description
        </h2>
        {property.description !== undefined ? (
          <p
            className="m-0 text-sm leading-relaxed"
            style={{ color: 'var(--fg-secondary)' }}
          >
            {property.description}
          </p>
        ) : (
          <p
            className="m-0 text-sm italic"
            style={{ color: 'var(--fg-tertiary)' }}
          >
            No description yet. Use the Content tab to add one — the
            AI generate endpoint ships with SCRUM-186.
          </p>
        )}
      </Card>
    </div>
  );
}

/* ============================================================
 * Per-tab chrome with NotConnectedYet bodies (SCRUM-171..177).
 *
 * Each tab renders the bundle's canonical section header + subtitle
 * + primary action (per `PROPERTY_DETAIL_SPEC.md`), then a
 * NotConnectedYet body citing the right BE-evolution follow-up.
 * Engineers reading the placeholder see the structural skeleton
 * even though the data isn't wired — same posture as the SCRUM-174
 * Spaces/Units/SIM "shell with chrome" doctrine in the plan.
 * ============================================================ */

interface TabHeaderProps {
  readonly title: string;
  readonly subtitle: string;
  readonly action?: { readonly label: string; readonly icon?: 'plus' | 'arrowUp' };
}

function TabHeader({
  title,
  subtitle,
  action,
}: TabHeaderProps): React.ReactElement {
  return (
    <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2
          className="m-0 text-lg font-semibold"
          style={{ color: 'var(--fg-primary)' }}
        >
          {title}
        </h2>
        <p
          className="m-0 mt-1 text-sm"
          style={{ color: 'var(--fg-secondary)' }}
        >
          {subtitle}
        </p>
      </div>
      {action !== undefined ? (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            alert(
              `${action.label} — not connected yet. Tracked under the section's follow-up ticket.`,
            );
          }}
        >
          {action.icon !== undefined ? (
            <Icon name={action.icon} size={14} aria-hidden />
          ) : null}
          <span className={action.icon !== undefined ? 'ml-2' : ''}>
            {action.label}
          </span>
        </Button>
      ) : null}
    </header>
  );
}

function PricingTab({
  property,
}: {
  readonly property: PropertyView;
}): React.ReactElement {
  return (
    <div>
      <TabHeader
        title="Base price & floor rates"
        subtitle="Base rates seed every channel. Floor rates cap how low dynamic pricing can drop."
      />
      {/* SCRUM-171 (P1.3) chrome shell: the BE exposes
          GET/PATCH /properties/:id/baseline + POST
          /properties/baseline/bulk, but the generated
          `@artemis/contracts` types the response body as `never`
          (BE OpenAPI schema gap). Until the contracts regen ships
          the typed body, render the bundle's rate-table layout
          with a placeholder row + NotConnectedYet banner. When the
          regen lands, swap to `useBaseline(propertyId)` over the
          real DTO. The regen itself is tracked under SCRUM-185. */}
      <Card padding="none">
        <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr
              className="text-left text-xs font-bold uppercase"
              style={{
                color: 'var(--fg-tertiary)',
                letterSpacing: '0.08em',
                background: 'var(--bg-sunken)',
              }}
            >
              <th scope="col" className="px-4 py-3">Unit</th>
              <th scope="col" className="px-4 py-3">WD base</th>
              <th scope="col" className="px-4 py-3">WE base</th>
              <th scope="col" className="px-4 py-3">WD floor</th>
              <th scope="col" className="px-4 py-3">WE floor</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderTop: '1px solid var(--border-subtle)' }}>
              <td className="px-4 py-3.5" style={{ color: 'var(--fg-primary)' }}>
                {property.name}
              </td>
              <td className="px-4 py-3.5" colSpan={4}>
                <span
                  className="text-sm italic"
                  style={{ color: 'var(--fg-tertiary)' }}
                >
                  Not connected yet — awaiting `@artemis/contracts` baseline DTO regeneration (SCRUM-185).
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function SpacesTab(): React.ReactElement {
  return (
    <div>
      <TabHeader
        title="Physical spaces"
        subtitle="The smallest controllable parts of this property. Spaces are not bookable on their own — they compose into units."
        action={{ label: 'Add space', icon: 'plus' }}
      />
      <NotConnectedYet surface="Spaces" ticket="SCRUM-188" />
    </div>
  );
}

function UnitsTab(): React.ReactElement {
  return (
    <div>
      <TabHeader
        title="Sellable units"
        subtitle="Bookable configurations built from one or more spaces. SIM auto-resolves conflicts."
        action={{ label: 'New unit', icon: 'plus' }}
      />
      {/* SCRUM-174 P2.1: SimDiagram skeleton renders against the
          bundle's canonical "Sea Cliff Estate" mock data so the
          structural a11y semantics + visual treatment are pinned
          today. The DATA is shell — when the BE grows units+spaces
          on `PropertyResponseDto`, callers pass real data; the
          component contract doesn't change. */}
      <Card padding="md" className="mb-3">
        <div className="flex items-center gap-2 mb-2">
          <span style={{ color: 'var(--brand-500)' }}>
            <Icon name="grid" size={16} aria-hidden />
          </span>
          <h3
            className="m-0 text-sm font-semibold"
            style={{ color: 'var(--fg-primary)' }}
          >
            Split Inventory Model — conflict map
          </h3>
        </div>
        <SimDiagram {...MOCK_SIM_DEMO} />
      </Card>
      <NotConnectedYet
        surface="Live units roster"
        ticket="SCRUM-188"
      />
    </div>
  );
}

function ChannelsTab(): React.ReactElement {
  return (
    <div>
      <TabHeader
        title="Channels"
        subtitle="Connect your inventory to Airbnb, VRBO, and Booking.com. Sync state per unit appears here."
      />
      <NotConnectedYet surface="Channels" ticket="SCRUM-187" />
    </div>
  );
}

function ContentTab({
  property,
}: {
  readonly property: PropertyView;
}): React.ReactElement {
  return (
    <div>
      <TabHeader
        title="Descriptions"
        subtitle={
          property.description !== undefined
            ? 'Shared description below. Per-channel overrides ship with Pro mode.'
            : 'No description yet. Per-channel overrides + AI generate ship with Pro mode.'
        }
      />
      <Card padding="md">
        {property.description !== undefined ? (
          <p
            className="m-0 text-sm leading-relaxed"
            style={{ color: 'var(--fg-secondary)' }}
          >
            {property.description}
          </p>
        ) : (
          <NotConnectedYet
            surface="Content tab + AI generate"
            ticket="SCRUM-186"
          />
        )}
      </Card>
    </div>
  );
}

function AmenitiesTab({ propertyId }: { propertyId: string }): React.ReactElement {
  return (
    <div>
      <TabHeader
        title="Amenities"
        subtitle="Select the amenities this property offers."
      />
      <AmenitiesPanel propertyId={propertyId} />
    </div>
  );
}

function ComplianceTab(): React.ReactElement {
  return (
    <div>
      <TabHeader
        title="Compliance"
        subtitle="STR license, VAT, tax id, house rules, local ordinance acknowledgement."
      />
      <NotConnectedYet surface="Compliance" ticket="SCRUM-189" />
    </div>
  );
}

function HistoryTab(): React.ReactElement {
  return (
    <div>
      <TabHeader
        title="History"
        subtitle="Reverse-chronological audit feed for this property."
      />
      <NotConnectedYet surface="History" ticket="SCRUM-190" />
    </div>
  );
}

type OwnerModal = 'open' | 'end' | 'transfer' | 'amend' | null;

/**
 * SCRUM-172 — wired Owner tab. Composes the current-owner card +
 * history timeline over `useOwnershipCurrent` / `useOwnershipHistory`,
 * resolving `userId → name` via `useOrgUsers`. The action affordances
 * open the matching modal and are gated to match the BE controller's
 * permission split: Add owner + Transfer need `property.write`; Amend
 * share + End ownership need `property.ownership.write`. Document UPLOAD
 * is a deferred fast-follow — the card already renders the existing
 * contract document + a real download link.
 */
function OwnerTab({ propertyId }: { readonly propertyId: string }): React.ReactElement {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;
  const { resolveOwnerName, members } = useOrgUsers();
  const current = useOwnershipCurrent(propertyId, resolveOwnerName);
  const history = useOwnershipHistory(propertyId, resolveOwnerName);
  const [modal, setModal] = React.useState<OwnerModal>(null);
  const close = (): void => setModal(null);

  const owner = current.status === 'ready' ? current.data : null;

  // Real download URL for the contract document (GET …/document → 303 →
  // file). Built from org slug + property/ownership ids; undefined when
  // there's no document or no org context (the card then shows the
  // filename as plain text).
  const documentHref =
    owner?.contractDocument !== undefined && orgSlug !== null
      ? `${env.apiBaseUrl}/organizations/${orgSlug}/properties/${propertyId}/ownership/${owner.id}/document`
      : undefined;

  return (
    <div>
      <TabHeader
        title="Ownership"
        subtitle="Current owner, ownership share, document trail, and transfer history."
      />

      {current.status === 'loading' ? (
        <Card padding="lg" aria-busy="true">
          <span className="sr-only">Loading ownership…</span>
          <div
            className="h-28 rounded-md"
            style={{ background: 'var(--bg-sunken)' }}
            aria-hidden
          />
        </Card>
      ) : current.status === 'forbidden' ? (
        <Card padding="md">
          <p className="m-0 text-sm" style={{ color: 'var(--fg-secondary)' }}>
            You don’t have permission to view ownership for this property.
          </p>
        </Card>
      ) : current.status === 'error' ? (
        <Card padding="md">
          <p className="m-0 mb-3 text-sm" style={{ color: 'var(--fg-secondary)' }}>
            Couldn’t load ownership.
          </p>
          <Button variant="secondary" size="sm" onClick={current.refetch}>
            Retry
          </Button>
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          <OwnershipCard
            ownership={owner}
            contractDocumentHref={documentHref}
            actions={
              // Permission split mirrors the BE controller exactly
              // (property-ownership.controller.ts): Open + Transfer
              // require `property.write`; End + Amend require
              // `property.ownership.write`. A `property_owner` (ownership
              // .write but NOT property.write) must NOT see Add owner /
              // Transfer — the API 403s them (authz regression spec).
              owner === null ? (
                <RequirePermission slug="property.write" fallback={null}>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setModal('open')}
                  >
                    <Icon name="plus" size={14} aria-hidden />
                    <span className="ml-2">Add owner</span>
                  </Button>
                </RequirePermission>
              ) : (
                <>
                  {/* Amend share is a Pro-only path: in Lite the BE no-ops
                      at 100% and throws LiteAmendShareNotSupportedError for
                      anything else, so the action is hidden entirely
                      (OWNERSHIP_LITE_MODE). Flips back on when Pro ships.
                      Still property.ownership.write when it returns. */}
                  {!OWNERSHIP_LITE_MODE ? (
                    <RequirePermission
                      slug="property.ownership.write"
                      fallback={null}
                    >
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setModal('amend')}
                      >
                        Amend share
                      </Button>
                    </RequirePermission>
                  ) : null}
                  <RequirePermission slug="property.write" fallback={null}>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setModal('transfer')}
                    >
                      Transfer
                    </Button>
                  </RequirePermission>
                  <RequirePermission
                    slug="property.ownership.write"
                    fallback={null}
                  >
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => setModal('end')}
                    >
                      End ownership
                    </Button>
                  </RequirePermission>
                </>
              )
            }
          />

          {owner !== null ? (
            <p
              className="m-0 text-xs italic"
              style={{ color: 'var(--fg-tertiary)' }}
            >
              Uploading a new contract document is coming soon (SCRUM-172
              follow-up). Existing documents are shown above with a download
              link.
            </p>
          ) : null}

          {history.status === 'ready' ? (
            <OwnershipHistory records={history.data} />
          ) : null}
        </div>
      )}

      {modal === 'open' ? (
        <OpenOwnershipModal
          propertyId={propertyId}
          members={members}
          onClose={close}
        />
      ) : null}
      {owner !== null && modal === 'amend' ? (
        <AmendShareModal propertyId={propertyId} ownership={owner} onClose={close} />
      ) : null}
      {owner !== null && modal === 'transfer' ? (
        <TransferOwnershipModal
          propertyId={propertyId}
          ownership={owner}
          members={members}
          onClose={close}
        />
      ) : null}
      {owner !== null && modal === 'end' ? (
        <EndOwnershipModal propertyId={propertyId} ownership={owner} onClose={close} />
      ) : null}
    </div>
  );
}

function LifecycleTab({
  property,
}: {
  readonly property: PropertyView;
}): React.ReactElement {
  const archived = property.archivedAt !== undefined;
  return (
    <div>
      <TabHeader
        title="Lifecycle"
        subtitle={
          archived
            ? 'This property is archived. Existing reservations are preserved; new bookings are blocked.'
            : 'Archive this property to block new bookings. Existing reservations are preserved.'
        }
      />
      <Card padding="md">
        {archived ? (
          <p className="m-0 text-sm" style={{ color: 'var(--fg-secondary)' }}>
            {`Archived at ${new Date(
              property.archivedAt as number,
            ).toLocaleString()}.`}
          </p>
        ) : (
          <>
            <p
              className="m-0 mb-3 text-sm"
              style={{ color: 'var(--fg-secondary)' }}
            >
              Archive is reversible — reactivate from the same surface when
              ready.
            </p>
            {/* Archive is NOT wired: `DELETE /properties/:id`'s response
                schema isn't in the openapi spec yet, so there's no honest
                way to confirm the round-trip. Rather than a button that
                fakes success + navigates away (Simon review #3), the action
                stays NotConnectedYet until the @artemis/contracts regen lands
                the typed DELETE (tracked under SCRUM-173). */}
            <NotConnectedYet surface="Archive property" ticket="SCRUM-173" />
          </>
        )}
      </Card>
    </div>
  );
}

function DetailLoadingSkeleton(): React.ReactElement {
  return (
    <Card padding="lg" aria-busy="true">
      <span className="sr-only">Loading property…</span>
      <div
        className="rounded-md mb-4 h-32"
        style={{ background: 'var(--bg-sunken)' }}
        aria-hidden
      />
      <div
        className="rounded-md h-52"
        style={{ background: 'var(--bg-sunken)' }}
        aria-hidden
      />
    </Card>
  );
}

function DetailNotFound({
  onBack,
}: {
  readonly onBack: () => void;
}): React.ReactElement {
  return (
    <Card padding="lg" className="text-center">
      <h2
        className="m-0 mb-1 text-base font-semibold"
        style={{ color: 'var(--fg-primary)' }}
      >
        Property not found.
      </h2>
      <p
        className="m-0 mb-4 text-sm"
        style={{ color: 'var(--fg-secondary)' }}
      >
        It may have been archived or the link is wrong.
      </p>
      <Button variant="secondary" onClick={onBack}>
        Back to Properties
      </Button>
    </Card>
  );
}

function DetailForbidden(): React.ReactElement {
  return (
    <Card padding="lg" className="text-center">
      <h2
        className="m-0 mb-1 text-base font-semibold"
        style={{ color: 'var(--fg-primary)' }}
      >
        You don’t have access to this property.
      </h2>
      <p className="m-0 text-sm" style={{ color: 'var(--fg-secondary)' }}>
        Ask an administrator to grant you the property.read permission.
      </p>
    </Card>
  );
}

function DetailError({
  onRetry,
}: {
  readonly onRetry: () => void;
}): React.ReactElement {
  return (
    <Card padding="lg" className="flex items-center justify-between">
      <div>
        <h2
          className="m-0 mb-1 text-base font-semibold"
          style={{ color: 'var(--fg-primary)' }}
        >
          Couldn’t load this property.
        </h2>
        <p className="m-0 text-sm" style={{ color: 'var(--fg-secondary)' }}>
          Check your connection and try again.
        </p>
      </div>
      <Button variant="secondary" onClick={onRetry}>
        Retry
      </Button>
    </Card>
  );
}
